import { Box, Button, CircularProgress, Tooltip, Typography, IconButton } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { ArrowBack, BlurCircularRounded as SampleIcon, Memory as SimulationIcon } from '@mui/icons-material';
import Link from 'next/link';

import { log } from '@/lib/utils/logger';
import { formatErrorMessage } from '@/lib/utils/apiErrors';
import { BaseTreeArea } from '@/components/tree/BaseTreeArea';
import { DetailAreaDispatcher } from '@/components/tree/DetailAreaDispatcher';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useTreeStore } from '@/stores/treeStore';
import { useTree } from '@/hooks/useTree';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { extractEntityIdFromDataId } from '@/lib/tree/treeUtils';
import { useBackendStore } from '@/stores/backendStore';
import { useAuthStore } from '@/stores/authStore';
import { useNodeActions, useActionExecutor } from '@/hooks/useNodeActions';
import { usePrivileges } from '@/hooks/usePrivileges';
import { resolveDataRouteRoot } from '@/lib/routing/dataRouteResolver';
import { applyCanonicalDataBrowseRootNodeMapping, DATA_ROUTE_ROOT_MODE_KEY } from '@/lib/tree/dataRouteTreeConstants';
import type { NodeType } from '@/types/tree/base';

/**
 * Props interface for the DataTree component
 */
interface DataTreeAreaProps {
  /** Canonical /data/{ID} path identifier */
  dataId: string;
}

/**
 * DataTree component displays a hierarchical view of a data package and its contents.
 * It consists of two main areas:
 * 1. A tree view on the left showing the data package hierarchy
 * 2. A detail area on the right showing information about the selected node
 *
 * @component
 * @param {DataTreeAreaProps} props - The component props
 * @returns {JSX.Element} The rendered DataTree component
 */
const DataTreeAreaInner: FC<DataTreeAreaProps> = ({ dataId }) => {
  // Use custom hooks for reactive state access
  const { selectedNode } = useTreeSelection();
  const { currentUser } = useAuthStore();
  const { isAuthorized } = usePrivileges();

  // Use tree functions (no performance impact)
  const { initializeTree, getDataPackageData, getProjectData, getPublicationData } = useTree();

  // Get action executor
  const executeActionForNode = useActionExecutor();

  // Get whole collections only when needed (with warnings)
  const { getNodeData } = useTreeStore();
  const systemConfig = useSystemConfig();
  const { backend, isInitialized: isBackendInitialized } = useBackendStore();

  // State to track the actual root node ID returned from initializeTree
  const [rootNodeId, setRootNodeId] = useState<string | null>(null);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [resolvedRoot, setResolvedRoot] = useState<{
    type: 'dataPackage' | 'publication' | 'project';
    id: string;
  } | null>(null);
  // Track which dataId has been successfully initialized
  const [initializedDataId, setInitializedDataId] = useState<string | null>(null);
  // Track which dataId we've attempted to initialize (prevents infinite retries on error)
  const [initializationAttemptedFor, setInitializationAttemptedFor] = useState<string | null>(null);
  // Targeted subscription for root node loading state
  const isRootLoaded = useTreeStore((state) => rootNodeId && state.loadedNodes.has(rootNodeId));

  useEffect(() => {
    // Wait for required dependencies
    if (!isBackendInitialized || !backend || !currentUser) {
      log.debug('[DataTreeArea] Waiting for backend and user to initialize');
      return;
    }

    // Prevent duplicate initialization or retry after error
    // Only initialize if we haven't successfully initialized this dataId
    // and we haven't already attempted (and failed) to initialize it
    if (initializedDataId === dataId || initializationAttemptedFor === dataId) {
      return;
    }

    const loadTree = async () => {
      try {
        setInitializationAttemptedFor(dataId); // Mark as attempted before starting
        const resolved = await resolveDataRouteRoot(backend, dataId);
        setResolvedRoot({ type: resolved.type, id: resolved.id });
        log.debug('[DataTreeArea] Resolved /data root target:', {
          dataId,
          resolvedType: resolved.type,
          resolvedId: resolved.id,
        });
        // Create default settings if user exists but has no settings
        const userSettings = {
          ...(currentUser.settings || {}),
          [DATA_ROUTE_ROOT_MODE_KEY]: true,
        } as any;
        const actualRootNodeId = await initializeTree(resolved.type, resolved.id, userSettings, resolved.data);
        if (actualRootNodeId) {
          setRootNodeId(actualRootNodeId);
          setInitializedDataId(dataId); // Mark as successfully initialized
          setInitializationError(null);
          log.debug('[DataTreeArea] Tree initialized successfully:', { dataId, rootNodeId: actualRootNodeId });
        } else {
          throw new Error('Tree initialization returned null');
        }
      } catch (error) {
        const errorMessage = formatErrorMessage(error);
        log.error('[DataTreeArea] Error initializing tree:', { dataId, error: errorMessage });
        setInitializationError(errorMessage);
        // Don't set initializedDataId - this prevents treating it as successfully initialized
        // initializationAttemptedFor already set above prevents infinite retries
      }
    };

    loadTree();
  }, [
    dataId,
    initializeTree,
    isBackendInitialized,
    backend,
    currentUser,
    initializedDataId,
    initializationAttemptedFor,
  ]);

  // Get the root data package node using the returned root node ID
  const rootNode = rootNodeId ? getNodeData(rootNodeId) : null;
  const rootDataPackageNode = rootNode;
  const dataPackage = rootNode ? getDataPackageData(rootNode) : undefined;
  const project = rootNode ? getProjectData(rootNode) : undefined;
  const publication = rootNode ? getPublicationData(rootNode) : undefined;

  // Get action handlers specifically for the root data package node
  const { addMenuActions: rootNodeAddActions } = useNodeActions(rootNode || undefined);

  // Find the add actions for the root data package node
  const addSamplesAction = rootNodeAddActions.find((action) => action.id === 'add-sample-to-data-package');
  const addSimulationAction = rootNodeAddActions.find((action) => action.id === 'add-theory-activity-to-data-package');

  // Check button visibility and disabled state based on actions
  const isDataPackageRoot = resolvedRoot?.type === 'dataPackage';
  const showAddSamplesButton =
    isDataPackageRoot && addSamplesAction && rootDataPackageNode
      ? addSamplesAction.isVisible(rootDataPackageNode, isAuthorized, () => dataPackage)
      : false;
  const isAddSamplesDisabled = !isDataPackageRoot
    ? true
    : addSamplesAction && rootDataPackageNode
      ? addSamplesAction.isDisabled?.(rootDataPackageNode, () => dataPackage) || false
      : false;

  const showAddSimulationButton =
    isDataPackageRoot && addSimulationAction && rootDataPackageNode
      ? addSimulationAction.isVisible(rootDataPackageNode, isAuthorized, () => dataPackage)
      : false;
  const isAddSimulationDisabled = !isDataPackageRoot
    ? true
    : addSimulationAction && rootDataPackageNode
      ? addSimulationAction.isDisabled?.(rootDataPackageNode, () => dataPackage) || false
      : false;

  // Handle add samples - execute the action on the root data package node
  const handleAddSamples = async () => {
    if (rootDataPackageNode && backend) {
      await executeActionForNode(rootDataPackageNode, 'add-sample-to-data-package', backend);
    }
  };

  // Handle add simulation - execute the action on the root data package node
  const handleAddSimulation = async () => {
    if (rootDataPackageNode && backend) {
      await executeActionForNode(rootDataPackageNode, 'add-theory-activity-to-data-package', backend);
    }
  };

  const rootTitle =
    resolvedRoot?.type === 'project'
      ? `Project ${project?.projectLabel ?? dataId}`
      : resolvedRoot?.type === 'publication'
        ? `Publication ${publication?.doi || publication?.title || dataId}`
        : `Data Package ${dataPackage?.label ?? dataId}`;

  // Show error message if initialization failed
  if (initializationError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Failed to load data tree
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {initializationError}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            // Allow retry by resetting the initialization state
            setInitializationError(null);
            setRootNodeId(null);
            setResolvedRoot(null);
            setInitializedDataId(null);
            setInitializationAttemptedFor(null);
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Show loading while backend is initializing or tree is loading
  if (!isBackendInitialized || !backend || !isRootLoaded || !rootNodeId || !rootNode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  const showAddButtons = isDataPackageRoot;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', minHeight: 0 }}
    >
      {/* Header with back button, title, and action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to Data Package Search">
            <span>
              <Link href={`${systemConfig?.basePath}/data`} passHref>
                <IconButton size="small" component="span">
                  <ArrowBack />
                </IconButton>
              </Link>
            </span>
          </Tooltip>
          <Typography variant="h5" sx={{ fontSize: '1.25rem' }}>
            {rootTitle}
          </Typography>
        </Box>

        {/* Action Buttons - positioned in tree area header */}
        {showAddButtons ? (
          <Box display="flex" gap={2}>
            {/* Add Samples Button - visible only if user has permission, disabled if published/submitted */}
            {showAddSamplesButton ? (
              <Tooltip title="Add samples to this data package">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SampleIcon />}
                    onClick={handleAddSamples}
                    disabled={isAddSamplesDisabled}
                    size="large"
                    sx={{ minWidth: '160px', fontWeight: 500 }}
                  >
                    Add Samples
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {/* Add Simulation Button - visible only if user has permission, disabled if published/submitted */}
            {showAddSimulationButton ? (
              <Tooltip title="Add simulations to this data package">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SimulationIcon />}
                    onClick={handleAddSimulation}
                    disabled={isAddSimulationDisabled}
                    size="large"
                    sx={{ minWidth: '160px', fontWeight: 500 }}
                  >
                    Add Simulation
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          gap: 2,
          p: 2,
          overflow: 'hidden',
        }}
      >
        {/* Left side: Tree View */}
        <Box
          sx={{
            width: '300px',
            minWidth: '300px',
            maxWidth: '300px',
            height: '100%',
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <BaseTreeArea
            rootType={applyCanonicalDataBrowseRootNodeMapping((resolvedRoot?.type ?? 'dataPackage') as NodeType, true)}
            rootId={extractEntityIdFromDataId(rootNodeId)}
            autoInitialize={false}
          />
        </Box>

        {/* Right side: Detail Area */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <DetailAreaDispatcher key={selectedNode?.nodeId} selectedNode={selectedNode} />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Remount tree UI when `dataId` changes so init/root/error state is fresh per package
 * (avoids `useEffect` that only resets state on prop change — satisfies `react-hooks/set-state-in-effect`).
 */
const DataTreeArea: FC<DataTreeAreaProps> = ({ dataId }) => <DataTreeAreaInner key={dataId} dataId={dataId} />;

export default DataTreeArea;
